import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const faqs = [
  {
    question: "What does Claw actually do for me?",
    answer: "Claw handles the everyday stuff that fills your day — scheduling appointments, drafting messages, setting reminders, researching travel, and keeping your life organised. You just message it like you would a friend, and it gets things done."
  },
  {
    question: "How do I talk to Claw?",
    answer: "However you prefer. Claw works via WhatsApp, iMessage, email, or a web chat. You don't need to learn any new apps or tools — just message it naturally."
  },
  {
    question: "How quickly can I get started?",
    answer: "From your setup call, Claw is typically live within 24 hours. We spend the first week learning how you communicate and what you care about, so it gets better every day."
  },
  {
    question: "Will it really sound like me?",
    answer: "Yes — that's one of the things Claw is best at. Every time you edit or approve a draft, it learns. By day 7, most users find Claw's messages need little to no editing."
  },
  {
    question: "Is my data safe and private?",
    answer: "Absolutely. Claw runs on dedicated infrastructure that's only yours — no shared servers, no data harvesting. For Premium users, we offer a fully local option where nothing ever leaves your home."
  },
  {
    question: "What if I want to cancel or stop?",
    answer: "You own your setup. There's no subscription to cancel. If you ever want to stop using Claw, everything is yours to keep or delete. No lock-in."
  },
];

const FAQItem = ({ faq, isOpen, onToggle }) => (
  <div className={`border rounded-xl overflow-hidden mb-3 transition-all duration-200 ${isOpen ? 'border-[#B8CDD9] bg-[#EBF2F9]/40' : 'border-[#E8E2DA] bg-white hover:border-[#D4CEC8]'}`}>
    <button className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" onClick={onToggle}>
      <span className="text-[15px] font-semibold text-[#1A1A18] pr-8 leading-snug">{faq.question}</span>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${isOpen ? 'bg-[#2D6A8F] text-white rotate-180' : 'bg-[#F0ECE8] text-[#6B6560]'}`}>
        <SafeIcon icon={FiIcons.FiChevronDown} className="text-sm" />
      </div>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25, ease: 'easeInOut' }}>
          <div className="px-6 pb-5 text-[#6B6560] text-sm leading-relaxed border-t border-[#E8E2DA] pt-4">
            {faq.answer}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section id="faq" className="py-32 bg-[#F8F6F3]">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="tag-pill mx-auto mb-5 w-fit">Common questions</div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-[#1A1A18] tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Good questions.
          </h2>
          <p className="font-handwritten text-xl text-[#C17B4A] mt-2" style={{ transform: 'rotate(-0.5deg)' }}>
            here are the honest answers ↓
          </p>
        </div>
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          {faqs.map((faq, idx) => (
            <FAQItem key={idx} faq={faq} isOpen={openIdx === idx} onToggle={() => setOpenIdx(openIdx === idx ? -1 : idx)} />
          ))}
        </motion.div>
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          transition={{ delay: 0.2 }} className="mt-10 text-center">
          <p className="text-[#6B6560] text-sm">
            Still unsure?{' '}
            <a href="https://api.leadconnectorhq.com/widget/booking/S442pJP90RpLtN5FrwSF" target="_blank" rel="noopener noreferrer" className="text-[#2D6A8F] hover:text-[#1E4D7A] font-medium underline underline-offset-4">
              Book a quick 15-min chat with the team.
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;